import React, { useEffect, useState, useMemo } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiTrendingUp,
  FiAlertCircle,
  FiDownload,
  FiX,
  FiCalendar,
  FiUser,
  FiMapPin,
  FiPhone,
  FiBriefcase,
  FiShoppingBag,
  FiUserCheck
} from "react-icons/fi";
import "../styles/PilotBookingStatus.css";

const GET_ALL_PILOT_BOOKINGS = gql`
  query GetAllPilotBookings {
    getAllPilotBookings {
      id
      bookingId
      pilotId
      pilotName
      pilotCompany
      pilotType
      buyerId
      buyerName
      buyerEmail
      contact
      location
      date
      startTime
      endTime
      status
      sellerId
      sellerName
      sellerEmail
      sellerPhone
      pilotOwnerId
      pilotOwnerType
      createdAt
      updatedAt
    }
  }
`;

// Export function as provided
export const exportToCSV = (headers, rows, filename = 'export.csv') => {
  if (!headers || !rows || rows.length === 0) {
    console.warn("ExportToCSV: No headers or data provided.");
    return;
  }

  const csvContent = [
    headers.join(","),
    ...rows.map(row =>
      row.map(cell => {
        if (cell === null || cell === undefined) return "";
        const cellStr = String(cell);
        // Escape quotes and wrap in quotes if it contains comma, quote or newline
        if (cellStr.includes('"') || cellStr.includes(',') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(",")
    )
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function PilotBookingStatus() {
  const { data, loading, error, refetch } = useQuery(GET_ALL_PILOT_BOOKINGS, {
    fetchPolicy: "network-only"
  });
  
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    if (data?.getAllPilotBookings) {
      const processedBookings = data.getAllPilotBookings
        .map(b => ({
          ...b,
          status: b.status?.toLowerCase(),
          pilotType: b.pilotType?.toLowerCase()
        }));
      
      setBookings(processedBookings);
      setFilteredBookings(processedBookings);
    }
  }, [data]);

  // Apply filters
  useEffect(() => {
    let results = [...bookings];
    
    // Search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(b => 
        b.pilotName?.toLowerCase().includes(term) ||
        b.buyerName?.toLowerCase().includes(term) ||
        b.contact?.toLowerCase().includes(term) ||
        b.location?.toLowerCase().includes(term) ||
        b.bookingId?.toLowerCase().includes(term) ||
        b.pilotCompany?.toLowerCase().includes(term) ||
        b.sellerName?.toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== "all") {
      results = results.filter(b => b.status === statusFilter);
    }
    
    // Type filter
    if (typeFilter !== "all") {
      results = results.filter(b => b.pilotType === typeFilter);
    }
    
    // Date filter
    if (dateFilter) {
      results = results.filter(b => {
        const bookingDate = parseDate(b.date || b.createdAt);
        if (!bookingDate) return false;
        const filterDate = new Date(dateFilter);
        return bookingDate.toDateString() === filterDate.toDateString();
      });
    }
    
    setFilteredBookings(results);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, typeFilter, dateFilter, bookings]);

  const parseDate = (value) => {
    if (!value) return null;
    if (!isNaN(value)) {
      if (value.toString().length === 10) return new Date(value * 1000);
      return new Date(Number(value));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = parseDate(dateString);
    if (!date) return "N/A";
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFullDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "—";
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "—";
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Statistics
  const stats = useMemo(() => {
    const total = bookings.length;
    const approved = bookings.filter(b => b.status === "approved" || b.status === "confirmed").length;
    const pending = bookings.filter(b => b.status === "pending").length;
    const rejected = bookings.filter(b => b.status === "rejected").length;
    const completed = bookings.filter(b => b.status === "completed").length;
    const seller = bookings.filter(b => b.pilotType === "seller").length;
    const buyer = bookings.filter(b => b.pilotType === "buyer").length;
    
    return { total, approved, pending, rejected, completed, seller, buyer };
  }, [bookings]);

  // Pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredBookings.slice(startIndex, endIndex);

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setTypeFilter("all");
    setDateFilter("");
  };

  const handleExport = () => {
    const headers = [
      "Booking ID",
      "Pilot Name",
      "Pilot Company",
      "Pilot Type",
      "Buyer Name",
      "Contact",
      "Location",
      "Date",
      "Start Time",
      "End Time",
      "Status",
      "Seller Name",
      "Created At"
    ];

    const rows = filteredBookings.map(b => [
      b.bookingId,
      b.pilotName || "",
      b.pilotCompany || "",
      b.pilotType?.charAt(0).toUpperCase() + b.pilotType?.slice(1),
      b.buyerName,
      b.contact,
      b.location,
      b.date,
      b.startTime,
      b.endTime,
      b.status?.charAt(0).toUpperCase() + b.status?.slice(1),
      b.sellerName || "",
      formatDateTime(b.createdAt)
    ]);

    exportToCSV(headers, rows, `pilot-bookings-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Keep type icon function (this is for seller/buyer type)
  const getTypeIcon = (type) => {
    switch(type) {
      case "seller":
        return <FiShoppingBag className="type-icon seller" />;
      case "buyer":
        return <FiUserCheck className="type-icon buyer" />;
      default:
        return <FiUser className="type-icon other" />;
    }
  };

  if (loading) return (
    <div className="booking-loading">
      <div className="premium-spinner"></div>
      <p>Loading pilot bookings...</p>
    </div>
  );
  
  if (error) return (
    <div className="booking-error">
      <div className="error-icon">
        <FiAlertCircle />
      </div>
      <p>Error loading data</p>
      <button onClick={() => refetch()} className="retry-btn">
        Try Again
      </button>
    </div>
  );

  return (
    <div className="booking-container premium">
      {/* Premium Header */}
      <div className="premium-header">
        <div className="header-main">
          <h1 className="premium-title">
            <FiTrendingUp className="title-icon" />
            Pilot Bookings
          </h1>
          <p className="premium-subtitle">Manage and track all pilot booking requests</p>
        </div>
        
        <div className="header-stats">
          <div className="stat-mini total">
            <div className="stat-mini-value">{stats.total}</div>
            <div className="stat-mini-label">Total</div>
          </div>
          <div className="stat-mini seller">
            <div className="stat-mini-value">{stats.seller}</div>
            <div className="stat-mini-label">Seller</div>
          </div>
          <div className="stat-mini buyer">
            <div className="stat-mini-value">{stats.buyer}</div>
            <div className="stat-mini-label">Buyer</div>
          </div>
          <div className="stat-mini approved">
            <div className="stat-mini-value">{stats.approved}</div>
            <div className="stat-mini-label">Approved</div>
          </div>
          <div className="stat-mini pending">
            <div className="stat-mini-value">{stats.pending}</div>
            <div className="stat-mini-label">Pending</div>
          </div>
          <div className="stat-mini completed">
            <div className="stat-mini-value">{stats.completed}</div>
            <div className="stat-mini-label">Completed</div>
          </div>
        </div>
        
        <div className="header-controls">
          <button className="premium-btn export" onClick={handleExport}>
            <FiDownload /> Export
          </button>
          <button className="premium-btn refresh" onClick={() => refetch()}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Premium Filter Bar */}
      <div className="premium-filter-bar">
        <div className="search-wrapper">
          <div className="search-icon">
            <FiSearch />
          </div>
          <input
            type="text"
            placeholder="Search bookings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="premium-search"
          />
          {searchTerm && (
            <button className="clear-btn" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <button 
            className={`filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>
          <div className="page-control">
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="premium-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="premium-advanced-filters">
          <div className="filter-row">
            <div className="filter-col">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            
            <div className="filter-col">
              <label>Pilot Type</label>
              <select 
                value={typeFilter} 
                onChange={(e) => setTypeFilter(e.target.value)}
                className="premium-select"
              >
                <option value="all">All Types</option>
                <option value="seller">Seller</option>
                <option value="buyer">Buyer</option>
              </select>
            </div>
            
            <div className="filter-col">
              <label>Booking Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <button className="reset-filters" onClick={resetFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Table */}
      <div className="premium-table-container">
        <div className="table-info-bar">
          <div className="table-count">
            {filteredBookings.length} bookings
          </div>
          <div className="table-navigation">
            Showing {Math.min(startIndex + 1, filteredBookings.length)}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length}
          </div>
        </div>
        
        <div className="table-scroll-wrapper">
          <table className="premium-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Type</th>
                <th>Pilot</th>
                <th>Buyer</th>
                <th>Location</th>
                <th>Date & Time</th>
                <th>Owner</th>
                <th>Status</th>
              </tr>
            </thead>
            
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    <FiAlertCircle />
                    <p>No bookings found</p>
                  </td>
                </tr>
              ) : (
                currentItems.map((booking) => (
                  <tr 
                    key={booking.bookingId} 
                    className="booking-row"
                    onClick={() => setSelectedBooking(booking)}
                  >
                    <td>
                      <div className="booking-id">#{booking.bookingId}</div>
                      <div className="booking-date">{formatDate(booking.createdAt)}</div>
                    </td>
                    <td>
                      <div className="type-cell">
                        {getTypeIcon(booking.pilotType)}
                        <span className={`type-badge ${booking.pilotType}`}>
                          {booking.pilotType === "seller" ? "Seller" : "Buyer"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="pilot-info">
                        <div className="customer-name">{booking.pilotName || "Unknown Pilot"}</div>
                        <div className="pilot-company">
                          <FiBriefcase /> {booking.pilotCompany || "Independent"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="buyer-info">
                        <div className="customer-name">{booking.buyerName}</div>
                        <div className="phone">
                          <FiPhone /> {booking.contact}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="location">
                        <FiMapPin /> {booking.location}
                      </div>
                    </td>
                    <td>
                      <div className="date-time">
                        <div className="date">
                          <FiCalendar /> {booking.date}
                        </div>
                        <div className="time">
                          {booking.startTime} - {booking.endTime}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="owner-info">
                        {booking.pilotType === "seller" ? (
                          <>
                            <div className="owner-name">{booking.sellerName}</div>
                            <div className="owner-email">{booking.sellerEmail}</div>
                          </>
                        ) : (
                          <div className="owner-id">Buyer ID: {booking.pilotOwnerId}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="status-cell">
                        {/* Status icon removed - only badge remains */}
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || "—"}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Premium Pagination */}
        {totalPages > 1 && (
          <div className="premium-pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-nav">
              <button 
                className="nav-btn first"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <FiChevronsLeft />
              </button>
              <button 
                className="nav-btn prev"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return pageNum <= totalPages ? (
                  <button
                    key={pageNum}
                    className={`nav-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ) : null;
              })}
              
              <button 
                className="nav-btn next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FiChevronRight />
              </button>
              <button 
                className="nav-btn last"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Modal */}
      {selectedBooking && (
        <div className="premium-modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="premium-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Booking Details</h3>
              <button className="modal-close" onClick={() => setSelectedBooking(null)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Booking ID</span>
                  <span className="detail-value">#{selectedBooking.bookingId}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Type</span>
                  <span className={`detail-value type ${selectedBooking.pilotType}`}>
                    {getTypeIcon(selectedBooking.pilotType)}
                    {selectedBooking.pilotType?.charAt(0).toUpperCase() + selectedBooking.pilotType?.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Pilot</span>
                  <span className="detail-value">
                    {selectedBooking.pilotName} ({selectedBooking.pilotCompany})
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Buyer</span>
                  <span className="detail-value">{selectedBooking.buyerName}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact</span>
                  <span className="detail-value">{selectedBooking.contact}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">{selectedBooking.location}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">{selectedBooking.date}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Time</span>
                  <span className="detail-value">
                    {selectedBooking.startTime} - {selectedBooking.endTime}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Owner</span>
                  <span className="detail-value">
                    {selectedBooking.pilotType === "seller" 
                      ? `${selectedBooking.sellerName} (${selectedBooking.sellerEmail})`
                      : `Buyer ID: ${selectedBooking.pilotOwnerId}`
                    }
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`detail-value status ${selectedBooking.status}`}>
                    {/* Status icon removed - only text remains */}
                    {selectedBooking.status?.charAt(0).toUpperCase() + selectedBooking.status?.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created</span>
                  <span className="detail-value">{formatDateTime(selectedBooking.createdAt)}</span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-action-btn" onClick={() => setSelectedBooking(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}